<script>
  /**
   * Entrance.svelte
   *
   * This is the cartoon guided entrance. The main job here is to dispatch 3 things:
   * 1. setLocalStream
   * 2. setSnapshot
   * 3. signIn
   *
   * Note about setLocalStream. In order to have the function to get the stream from the local webcam available
   * to both Main (for debug, skip entrance) and also here in Entrance, the getLocalStream function is declared
   * in Main and passed in as a prop to Entrance. Then that function is used to dispatch setLocalStream back up
   * to Main. It's maybe a little convoluted but it's not too bad and it works well.
   *
   */
  import { onMount, tick, createEventDispatcher } from 'svelte';
  import { scale, fade, blur } from 'svelte/transition';
  import { quadIn, quadOut } from 'svelte/easing';
  import cookie from '../helpers/cookie';
  import { getLocalStream } from '../helpers/utils';
  import Cartoon from './Cartoon.svelte';
  import Mirror from './Mirror.svelte';
  import { ui, token } from '../stores';
  import Icon from './Icon.svelte';
  import { mdiRefresh } from '@mdi/js';

  const dispatch = createEventDispatcher();

  let cartoonEl,
    videoEl,
    snapshotEl,
    state,
    webcamPermissions,
    countdownTimer,
    localStream,
    count = '',
    snapshot;

  let skipTutorial = cookie.get('skipTutorial');

  $: theme = $ui.theme || $ui.defaultTheme;

  const shutterSound = new Audio('/audio/shutter.mp3');
  const beepSound = new Audio('/audio/beep.mp3');
  beepSound.volume = 0.2;

  // Keep a map of next states so we can skip current state with `skip(state)`
  function skip(state) {
    const next = {
      checkingAudio: () => getWebcam(),
      explainingSnapshot: () => setSnapshot(),
      reviewing: () => enterPub(),
      letsGo: () => signIn(),
    };
    if (state in next) {
      next[state]();
    }
  }

  function showStart() {
    setTimeout(() => {
      state = 'start';
    }, 100);
  }

  function checkAudio() {
    // Turn it off, wait a moment, turn it on again (so "try again" button will work).
    state = '';
    setTimeout(() => {
      state = 'checkingAudio'; // 'explainingSnapshot';
    }, 100);
  }

  async function checkWebcam() {
    const cam = await navigator.permissions.query({ name: 'camera' });
    const mic = await navigator.permissions.query({ name: 'microphone' });
    // console.log(cam.state, mic.state);

    // Granted?
    if (cam.state == 'granted' && mic.state == 'granted') {
      webcamPermissions = 'granted';
    }
    // Prompt?
    else if (cam.state == 'prompt' && mic.state == 'prompt') {
      webcamPermissions = 'prompt';
    }
    // Failed
    else {
      webcamPermissions = 'failed';
    }
    return webcamPermissions;
  }

  async function getWebcam() {
    if (webcamPermissions == 'granted') {
      await getStream();
      state = skipTutorial ? 'viewingMirror' : 'explainingSnapshot';
    }
    if (webcamPermissions == 'prompt') {
      explainWebcam();
    }
    if (webcamPermissions == 'failed') {
      state = 'webcamFailed';
    }
  }

  function explainWebcam() {
    state = 'explainingWebcam';
    setTimeout(async () => {
      await getStream();
      state = skipTutorial ? 'viewingMirror' : 'explainingSnapshot';
    }, 4000);
  }

  async function getStream() {
    // This will attach the localStream to the peer-io object too.
    localStream = await getLocalStream(); // Set to global context
    await tick();
    videoEl.srcObject = localStream; // Set to hidden video so we can capture snapshot
  }

  async function setSnapshot() {
    state = 'flashing';
    try {
      clearInterval(countdownTimer);
      const canvas = document.createElement('canvas');
      await tick();
      canvas.width = 400;
      canvas.height = 400;
      const context = canvas.getContext('2d');
      context.drawImage(videoEl, -140, -40, 640, 480);
      shutterSound.play();
      snapshot = canvas.toDataURL('image/jpeg', 0.6);
      await tick();
      $token.snapshot = snapshot;
      reviewSnapshot(snapshot);
    } catch (error) {
      console.error(error);
    }
  }

  async function reviewSnapshot(snapshot) {
    state = 'reviewing';
    await tick();
    try {
      snapshotEl.setAttribute('src', snapshot);
    } catch (error) {
      console.error(error);
    }
  }

  function signIn() {
    console.log('Got SIGN IN');
    $token.stream = localStream;
    dispatch('signIn');
  }

  function handleKeys(e) {
    // Space bar to skip through entrance steps
    if (e.keyCode == 13) {
      e.preventDefault();
      skip(state);
    }
  }

  function enterPub() {
    cookie.set('skipTutorial', true); // Set new or reset for another 7 days.
    if (skipTutorial) {
      signIn();
    } else {
      state = 'letsGo';
    }
  }

  onMount(async () => {
    ui.setTheme('public');
    webcamPermissions = await checkWebcam();
    if (skipTutorial) {
      await getWebcam();
      state = 'viewingMirror';
    } else {
      // disable this to fix NotAllowedError in Chrome:
      // "play() failed because the user didn't interact with the document first..."
      // checkAudio();
      showStart();
    }
  });
</script>

<style type="text/postcss">
  .button-wrapper {
    @apply absolute left-1/2 top-1/2 w-64 -ml-32 flex flex-col items-center;
    margin-top: 208px;
  }
</style>

<svelte:window on:keydown="{handleKeys}" />
<div id="Entrance" class="absolute flex left-0 top-0 w-full h-full z-10">
  <!-- Preload videos -->
  <video class="hidden" preload="auto" src="/video/check-audio.mp4"></video>
  <video class="hidden" preload="auto" src="/video/explain-webcam.mp4"></video>
  <video class="hidden" preload="auto" src="/video/explain-snapshot.mp4"></video>
  <video class="hidden" preload="auto" src="/video/okay-lets-go.mp4"></video>

  {#if state == 'start'}
    <div
      id="Cartoon"
      class="absolute left-1/2 top-1/2"
      style="width: 416px; height: 416px; margin-left: -208px; margin-top: -208px"
    >
      <div class="flex items-center justify-between">
        <button
          class="m-auto text-4xl text-white font-bold mt-32 py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          style="background:#4C6993"
          type="button"
          on:click={checkAudio}>
          Launch
        </button>
      </div>
    </div>
  {/if}

  <!-- Check Audio -->
  {#if state == 'checkingAudio'}
    <Cartoon
      bind:this="{cartoonEl}"
      stream="/video/check-audio.mp4"
      inParams="{{ delay: 600, duration: 400 }}" />
    <div class="button-wrapper" in:fade="{{ delay: 4000 }}">
      <button
        class="button"
        style="background: {theme.buttonPrimary.background}; color: {theme.buttonPrimary.text}"
        on:click="{getWebcam}">
        Yep, I hear you
      </button>
      <button class="text-button" on:click="{checkAudio}">Try again</button>
    </div>
  {/if}

  <!-- Explain webcam permissions -->
  {#if state == 'explainingWebcam'}
    <Cartoon bind:this="{cartoonEl}" stream="/video/explain-webcam.mp4" />
  {/if}

  <!-- Webcam failed -->
  {#if state == 'webcamFailed'}
    <Cartoon bind:this="{cartoonEl}" stream="/video/webcam-failed.mp4" />
  {/if}

  <!-- Explain snapshot -->
  {#if state == 'explainingSnapshot'}
    <Cartoon
      bind:this="{cartoonEl}"
      stream="/video/explain-snapshot.mp4"
      on:cartoonEnded="{() => (state = 'viewingMirror')}"
      outParams="{{ duration: 500 }}" />
  {/if}

  <!-- Mirror with optional Flash, or Review -->
  {#if ['viewingMirror', 'flashing', 'reviewing'].includes(state)}
    <Mirror stream="{localStream}">
      <!-- Viewing Mirror -->
      {#if state == 'viewingMirror'}
        <div class="flex justify-center">
          <span
            class="absolute flex top-0 left-0 justify-center items-center text-white font-bold
            opacity-50"
            style="width: 416px; height: 416px; font-size: 280px;"
            transition:scale="{{ duration: 160 }}">
            {#if count}{count}{/if}
          </span>
          {#if state == 'viewingMirror'}
            <button
              class="button mt-5"
              style="background: {theme.buttonPrimary.background}; color: {theme.buttonPrimary.text}"
              on:click="{setSnapshot}">
              Take a selfie
            </button>
          {/if}
        </div>

        <!-- Flash -->
      {:else if state == 'flashing'}
        <div
          class="absolute left-0 top-0 z-20 rounded-full bg-white"
          style="width: 416px; height: 416px; box-shadow: 0 0 80px 40px rgba(255, 255, 255, 0.5)"
          out:fade="{{ duration: 500, easing: quadOut }}"></div>

        <!-- Review -->
      {:else if state == 'reviewing'}
        <img
          class="absolute top-0 left-0 rounded-full border-8"
          style="max-width: unset; border-color: transparent;"
          bind:this="{snapshotEl}"
          in:blur="{{ duration: 900, easing: quadOut, radius: 5 }}"
          alt="Snapshot" />
        <div class="button-wrapper" in:fade="{{ delay: 1000 }}">
          <button
            class="button"
            style="background: {theme.buttonPrimary.background}; color: {theme.buttonPrimary.text}"
            on:click="{enterPub}">
            Looks good!
          </button>
          <button class="text-button -ml-5" on:click="{setSnapshot}">
            <span class="float-left w-12">
              <Icon path="{mdiRefresh}" style="fill: white; width: 4rem;" />
            </span>
            Retake
          </button>
        </div>
      {/if}
    </Mirror>
  {/if}

  <!-- Let's go! -->
  {#if state == 'letsGo'}
    <Cartoon
      bind:this="{cartoonEl}"
      stream="/video/okay-lets-go.mp4"
      on:cartoonEnded="{signIn}"
      inParams="{{ delay: 500, duration: 500 }}"
      outParams="{{ duration: 500 }}" />
  {/if}
</div>

<!-- Keep a hidden video element here so we can capture snapshot -->
<video
  class="absolute"
  style="visibility: hidden"
  bind:this="{videoEl}"
  muted="muted"
  autoplay
  playsinline></video>
